import { Box, Fade, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import React, { memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  carHotSpotEnableSet,
  getActiveTab,
  getCarHotSpotEnable,
} from '../../store/car-slice';

import { getCarStatus } from '../../store/car-slice';
import CardHistory from '../card-history/card-history';
import CardWarranty from '../card-warranty/card-warranty';
import { HotspotButton } from '../../components';

import ZoomButton from '../../components/zoom-button/zoom-button';
import FloatButtons from './float-buttons';

import theme from '../../config/theme';
import FabControlled from '../../components/fab/fab-controlled';
import SettingIcon from '../../assets/icon/setting-icon';

const ContainerFeatureStyle = styled(Box)<any>(({ orientation, page }) => ({
  width: 'fit-content',
  height: 'fit-content',
  display: 'flex',
  flexDirection: 'column-reverse',
  justifyContent: 'space-around',
  gap: '1rem',
  position: page === 'CAR' ? 'fixed' : 'absolute',
  top: page === 'CAR' ? '50%' : 'unset',
  left: page === 'CAR' ? '1rem' : '0',
  bottom: page === 'CAR' ? '1rem' : '0',
  transform: page === 'CAR' ? 'translateY(-50%)' : 'none',
  zIndex: 1,
  [theme.breakpoints.down('lg')]: {
    width: '100%',
    position:
      orientation === 'portrait' && page === 'CAR'
        ? 'initial'
        : orientation === 'portrait' && page === 'VDP'
          ? 'absolute'
          : 'fixed',
    left: page === 'CAR' ? '1rem' : '0.5rem',
    bottom: page === 'CAR' ? '1rem' : '0.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    left: page === 'CAR' ? '1rem' : '0.5rem',
  },
}));

type PropsTypes = {
  currentScale?: any;
  handleClickZoom?: any;
};

function ExteriorButtonContainer({
  currentScale,
  handleClickZoom,
}: PropsTypes) {
  // Selector
  const activeTab = useSelector(getActiveTab);
  const carHotSpotEnable = useSelector(getCarHotSpotEnable);
  const carStatus = useSelector(getCarStatus);

  // Hook
  const dispatch = useDispatch();
  const [mobileFeatureChecked, setMobileFeatureChecked] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [openWarranty, setOpenWarranty] = useState(false);

  // Media queries
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('lg'));
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const isLandscape = useMediaQuery('(orientation: landscape)');

  /**
   * toggle hotspot switch
   */
  const handleChange = () => {
    dispatch(carHotSpotEnableSet(carHotSpotEnable ? false : true));
  };

  const handleToggleWarranty = () => setOpenWarranty(!openWarranty);

  const handleToggleHistory = () => setOpenHistory(!openHistory);

  /**
   * toggle mobile info button
   */
  const handleClick = () => {
    setMobileFeatureChecked(!mobileFeatureChecked);
  };

  const settingIcon = carStatus === 'CAR' && (
    <Box
      sx={{
        position: isMobile && isLandscape ? 'fixed' : 'initial',
        bottom: '.6rem',
        right: '1rem',
      }}
    >
      {currentScale === 1 &&
        isMobile &&
        (!mobileFeatureChecked ? (
          <FabControlled
            Icon={SettingIcon}
            iconSizeMobile="0.9rem"
            onClick={handleClick}
          />
        ) : (
          <FabControlled Icon={CloseRoundedIcon} onClick={handleClick} />
        ))}
    </Box>
  );

  const floatButtons = carStatus === 'CAR' &&
    currentScale === 1 &&
    isMobile && (
      <Fade in={mobileFeatureChecked}>
        <Box>
          <FloatButtons />
        </Box>
      </Fade>
    );

  const hotspotButton = (
    <Fade in={currentScale === 1}>
      <Box>
        <HotspotButton checked={carHotSpotEnable} onChange={handleChange} />
      </Box>
    </Fade>
  );

  const zoomButton = carStatus === 'CAR' && (
    <ZoomButton
      handleClickZoom={handleClickZoom}
      isZoomEnabled={currentScale > 1}
      section={isMobile && isPortrait ? 'exterior-mobile' : 'exterior-web'}
    />
  );

  const mobileButtons = activeTab === 'exterior' && (
    <ContainerFeatureStyle
      orientation={isPortrait ? 'portrait' : 'landscape'}
      page={carStatus}
    >
      {isPortrait && floatButtons}

      <Box
        alignItems={'center'}
        display={'flex'}
        height={'100%'}
        justifyContent={'space-between'}
        margin={isMobile && isPortrait && carStatus === 'CAR' ? '0 .35rem' : 0}
      >
        {isPortrait && settingIcon}

        {isMobile && isPortrait && currentScale === 2 ? null : zoomButton}
        {carStatus === 'CAR' && isPortrait && hotspotButton}
      </Box>
    </ContainerFeatureStyle>
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {isLandscape && floatButtons}
        {isLandscape && settingIcon}
        {carStatus === 'CAR' && isLandscape && hotspotButton}
        {mobileButtons}
        <CardHistory onClose={handleToggleHistory} open={openHistory} />
        <CardWarranty onClose={handleToggleWarranty} open={openWarranty} />
      </Box>
      {isMobile && isPortrait && currentScale === 2 && (
        <Box sx={{ position: 'absolute', bottom: '-2.5rem' }}>{zoomButton}</Box>
      )}
    </>
  );
}

export default memo(ExteriorButtonContainer);
